import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, MapPin, X, ArrowRight, Sparkles, Archive } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";

/**
 * Phase 8.11 — EventsFlyerGrid
 *
 * Mobile-first, premium homepage events band. Sits directly under the hero.
 *
 * • Portrait flyer thumbnails in a responsive grid (2 cols mobile → 3 tablet → 4 desktop)
 * • Hover: subtle lift + gold glow reveals date/location overlay
 * • Click: opens a full-flyer modal with dark backdrop, Đăng ký CTA, close on Esc/backdrop
 * • Sort dynamic by event_date: upcoming (highlighted) above past (muted)
 * • Past events auto-slide down when their date rolls over — no code change needed
 * • Fully keyboard-accessible: role=button, Enter/Space activate, Esc closes modal
 */
export default function EventsFlyerGrid({ events = [] }) {
  const { lang } = useI18n();
  const L = (vi, en) => (lang === "en" ? en : vi);
  const [selected, setSelected] = useState(null);

  const { upcoming, past } = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const src = (events || []).filter((e) => e && e.slug);
    const up = src
      .filter((e) => (e.event_date || "") >= today && !e.archived)
      .sort((a, b) => (a.event_date || "").localeCompare(b.event_date || ""));
    const pa = src
      .filter((e) => (e.event_date || "") < today || e.archived)
      .sort((a, b) => (b.event_date || "").localeCompare(a.event_date || ""));
    return { upcoming: up, past: pa };
  }, [events]);

  useEffect(() => {
    if (!selected) return;
    const onKey = (e) => e.key === "Escape" && setSelected(null);
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [selected]);

  if (upcoming.length === 0 && past.length === 0) return null;

  return (
    <section
      data-testid="events-flyer-grid"
      className="relative bg-gradient-to-b from-warmgray-50 via-white to-warmgray-50 py-14 md:py-20"
    >
      {/* Subtle decorative accents */}
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-gold-400/40 to-transparent pointer-events-none" />
      <div className="absolute top-24 -left-24 w-64 h-64 rounded-full bg-emerald2/5 blur-3xl pointer-events-none" />
      <div className="absolute bottom-24 -right-24 w-64 h-64 rounded-full bg-gold-400/5 blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        {upcoming.length > 0 && (
          <div className="mb-16">
            <SectionHeader
              kicker={L("Sự kiện sắp diễn ra", "Upcoming events")}
              title={L("Đăng ký ngay – chỗ có hạn", "Reserve your spot – limited seats")}
              icon={<Sparkles className="w-3.5 h-3.5" />}
              tone="live"
            />
            <div
              data-testid="events-upcoming-grid"
              className="grid gap-4 sm:gap-5 md:gap-6 grid-cols-2 md:grid-cols-3 lg:grid-cols-3"
            >
              {upcoming.map((ev) => (
                <FlyerCard
                  key={ev.slug}
                  event={ev}
                  tone="upcoming"
                  onClick={() => setSelected(ev)}
                  L={L}
                />
              ))}
            </div>
          </div>
        )}

        {past.length > 0 && (
          <div>
            <SectionHeader
              kicker={L("Sự kiện đã diễn ra", "Past events")}
              title={L("Cùng nhìn lại hành trình", "Highlights from our journey")}
              icon={<Archive className="w-3.5 h-3.5" />}
              tone="past"
            />
            <div
              data-testid="events-past-grid"
              className="grid gap-4 sm:gap-5 md:gap-6 grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
            >
              {past.map((ev) => (
                <FlyerCard
                  key={ev.slug}
                  event={ev}
                  tone="past"
                  onClick={() => setSelected(ev)}
                  L={L}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {selected && (
        <FlyerModal
          event={selected}
          onClose={() => setSelected(null)}
          isPast={
            past.some((e) => e.slug === selected.slug) ||
            !!selected.archived ||
            (selected.event_date || "") < new Date().toISOString().slice(0, 10)
          }
          L={L}
        />
      )}
    </section>
  );
}

/* ─── SUB-COMPONENTS ─────────────────────────────────────────────────────── */

function SectionHeader({ kicker, title, icon, tone }) {
  const dot =
    tone === "live"
      ? "bg-emerald2 shadow-[0_0_16px_rgba(14,91,79,0.6)]"
      : "bg-espresso/40";
  return (
    <header className="mb-6 md:mb-8">
      <div className="flex items-center gap-2 mb-2">
        <span className={`inline-flex w-2 h-2 rounded-full ${dot}`} />
        <span className="inline-flex items-center gap-1.5 text-[10px] sm:text-[11px] uppercase tracking-[0.28em] font-semibold text-espresso/70">
          {icon} {kicker}
        </span>
      </div>
      <h2 className="text-2xl sm:text-3xl md:text-4xl font-serif text-charcoal leading-tight">
        {title}
      </h2>
    </header>
  );
}

function formatDate(iso, lang) {
  if (!iso) return "";
  try {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString(lang === "en" ? "en-US" : "vi-VN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function FlyerCard({ event, tone, onClick, L }) {
  const isPast = tone === "past";
  const img = event.hero_image_url || event.banner_image_url || "";
  const dateLabel = formatDate(event.event_date, "vi");
  return (
    <button
      type="button"
      onClick={onClick}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onClick()}
      data-testid={`event-flyer-card-${event.slug}`}
      className={`group relative block w-full text-left overflow-hidden rounded-2xl bg-white border cursor-pointer will-change-transform focus:outline-none focus:ring-2 focus:ring-gold-400 focus:ring-offset-2 transition-[transform,box-shadow,border-color] duration-300 ease-out ${
        isPast
          ? "border-warmgray-200 shadow-sm hover:shadow-2xl hover:-translate-y-1.5 hover:border-gold-300"
          : "border-warmgray-200/70 shadow-md hover:shadow-[0_25px_60px_-15px_rgba(184,138,71,0.45)] hover:-translate-y-2 hover:border-gold-400"
      }`}
      aria-label={`${event.name} — ${dateLabel}`}
    >
      {/* Premium gold glow ring — appears on hover only (upcoming events) */}
      {!isPast && (
        <span className="pointer-events-none absolute -inset-px rounded-2xl bg-gradient-to-br from-gold-400/0 via-gold-400/0 to-gold-400/0 group-hover:from-gold-400/30 group-hover:via-transparent group-hover:to-gold-400/30 transition-all duration-500" />
      )}

      {/* Portrait flyer image with subtle zoom (aspect 2:3 keeps text legible) */}
      <div
        className="relative w-full overflow-hidden bg-warmgray-100 rounded-2xl"
        style={{ aspectRatio: "2 / 3" }}
      >
        {img ? (
          <img
            src={img}
            alt={event.name}
            loading="lazy"
            className={`w-full h-full object-cover will-change-transform transition-transform duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.08] ${
              isPast ? "grayscale-[35%] group-hover:grayscale-0 transition-[filter,transform] duration-700" : ""
            }`}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-warmgray-400 text-xs">
            {L("Không có hình", "No image")}
          </div>
        )}

        {/* Base bottom-gradient (always visible) — for date + location legibility */}
        <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-black/85 via-black/40 to-transparent pointer-events-none" />

        {/* Hover overlay — full-card fade-in with event name + View CTA (desktop) */}
        <div className="hidden md:flex absolute inset-0 flex-col justify-end p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-400 bg-gradient-to-t from-black/90 via-black/45 to-transparent pointer-events-none">
          <div className="translate-y-4 group-hover:translate-y-0 transition-transform duration-500 ease-out">
            <div className="text-[10px] uppercase tracking-[0.28em] font-semibold text-gold-300 mb-1.5 line-clamp-1">
              {event.location}
            </div>
            <div className="text-white font-serif text-base leading-tight line-clamp-2 mb-3">
              {event.name}
            </div>
            <span className="inline-flex items-center gap-1.5 bg-gold-400 text-charcoal rounded-full px-4 py-1.5 text-[11px] font-semibold uppercase tracking-widest shadow-2xl">
              {isPast ? L("Xem lại", "View recap") : L("Xem flyer", "View flyer")}
              <ArrowRight className="w-3.5 h-3.5" />
            </span>
          </div>
        </div>

        {/* "Sắp diễn ra" / "Đã diễn ra" badge — top right */}
        <div className="absolute top-2.5 right-2.5 z-10">
          {isPast ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-white/85 backdrop-blur px-2.5 py-1 text-[10px] uppercase tracking-widest font-semibold text-espresso/70">
              {L("Đã diễn ra", "Past")}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald2 text-white px-2.5 py-1 text-[10px] uppercase tracking-widest font-semibold shadow-lg">
              <span className="relative flex h-1.5 w-1.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white" />
              </span>
              {L("Sắp tới", "Live")}
            </span>
          )}
        </div>

        {/* Bottom caption — always visible on mobile, fades out on desktop hover to reveal the overlay */}
        <div className="absolute inset-x-0 bottom-0 p-2.5 sm:p-3 text-white md:group-hover:opacity-0 transition-opacity duration-300 z-[5]">
          <div className="flex items-center gap-1.5 text-[10px] sm:text-[11px] font-medium opacity-95">
            <Calendar className="w-3 h-3" />
            <span className="tabular-nums">{dateLabel}</span>
          </div>
          <div className="flex items-center gap-1.5 text-[10px] sm:text-[11px] opacity-85 mt-0.5">
            <MapPin className="w-3 h-3 flex-shrink-0" />
            <span className="truncate">{event.location}</span>
          </div>
        </div>
      </div>
    </button>
  );
}

function FlyerModal({ event, onClose, isPast, L }) {
  const img = event.hero_image_url || event.banner_image_url || "";
  const dateLabel = formatDate(event.event_date, "vi");
  return (
    <div
      data-testid="event-flyer-modal"
      role="dialog"
      aria-modal="true"
      aria-label={event.name}
      className="fixed inset-0 z-[80] flex items-center justify-center px-3 sm:px-6 py-4 sm:py-8 bg-black/85 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
    >
      {/* Close button — fixed top-right so it's reachable on any screen */}
      <button
        type="button"
        onClick={onClose}
        data-testid="event-flyer-modal-close"
        aria-label={L("Đóng", "Close")}
        className="fixed top-3 right-3 sm:top-5 sm:right-5 z-10 inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/10 hover:bg-white/25 text-white transition"
      >
        <X className="w-5 h-5" />
      </button>

      {/* Content — click doesn't propagate to the backdrop */}
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-4xl max-h-full flex flex-col md:flex-row gap-4 md:gap-6 items-stretch"
      >
        {/* Flyer image */}
        <div className="flex-1 min-h-0 flex items-center justify-center">
          {img ? (
            <img
              src={img}
              alt={event.name}
              data-testid="event-flyer-modal-image"
              className="max-w-full max-h-[calc(100vh-160px)] md:max-h-[calc(100vh-80px)] w-auto h-auto object-contain rounded-xl shadow-2xl"
            />
          ) : null}
        </div>

        {/* Side / bottom panel — info + CTA */}
        <div className="md:w-72 md:min-w-72 shrink-0 flex flex-col justify-center gap-3 rounded-2xl bg-white/95 backdrop-blur px-4 py-4 md:px-5 md:py-6 text-charcoal shadow-2xl">
          <div className="text-[10px] uppercase tracking-[0.28em] font-semibold text-emerald2">
            {isPast ? L("Sự kiện đã diễn ra", "Past event") : L("Sự kiện sắp tới", "Upcoming event")}
          </div>
          <h3
            data-testid="event-flyer-modal-title"
            className="text-lg md:text-xl font-serif leading-tight"
          >
            {event.name}
          </h3>
          <div className="space-y-1.5 text-sm">
            <div className="flex items-center gap-2 text-espresso">
              <Calendar className="w-4 h-4 text-emerald2" />
              <span className="font-medium tabular-nums" data-testid="event-flyer-modal-date">{dateLabel}</span>
            </div>
            <div className="flex items-start gap-2 text-espresso">
              <MapPin className="w-4 h-4 text-emerald2 mt-0.5 flex-shrink-0" />
              <div className="min-w-0">
                <div className="font-medium truncate" data-testid="event-flyer-modal-location">{event.location}</div>
                {event.venue_name ? (
                  <div className="text-xs text-espresso/70 truncate">{event.venue_name}</div>
                ) : null}
              </div>
            </div>
          </div>
          <div className="pt-2 flex flex-col gap-2">
            {!isPast ? (
              <Link
                to={`/events/${event.slug}`}
                onClick={onClose}
                data-testid="event-flyer-modal-cta"
                className="inline-flex items-center justify-center gap-1.5 bg-emerald2 text-white rounded-full px-5 py-2.5 text-sm font-semibold uppercase tracking-[0.2em] hover:bg-emerald2-dark transition"
              >
                {L("Đăng ký ngay", "Register now")} <ArrowRight className="w-4 h-4" />
              </Link>
            ) : (
              <Link
                to={`/events/${event.slug}`}
                onClick={onClose}
                data-testid="event-flyer-modal-cta-past"
                className="inline-flex items-center justify-center gap-1.5 bg-charcoal text-white rounded-full px-5 py-2.5 text-sm font-semibold uppercase tracking-[0.2em] hover:bg-black transition"
              >
                {L("Xem chi tiết", "View details")} <ArrowRight className="w-4 h-4" />
              </Link>
            )}
            <button
              type="button"
              onClick={onClose}
              className="text-[11px] uppercase tracking-[0.24em] font-semibold text-espresso/60 hover:text-espresso transition py-1"
            >
              {L("Đóng", "Close")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
